import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, TextField, Paper, Alert, LinearProgress,
  MenuItem, Radio, RadioGroup, FormControlLabel, FormControl, Divider
} from '@mui/material';
import { CloudUpload as UploadIcon, Email as EmailIcon } from '@mui/icons-material';
import { ApiService } from '../services/api';

interface MailwhizList {
  uid: string;
  name: string;
}

interface ImportResult {
  listUid: string;
  createdNewList: boolean;
  totalRows: number;
  emailsFound: number;
  added: number;
  failed: number;
  skippedNoEmail: number;
  skippedDuplicate: number;
  failures: { email: string; reason: string }[];
}

const emptyNewList = {
  name: '', description: '', fromName: '', fromEmail: '', replyTo: '',
  companyName: '', address: '', city: '', state: '', countryCode: 'US'
};

const MailwhizImportPage: React.FC = () => {
  const API_BASE = ApiService.OUTREACH_API_BASE_URL;

  const [file, setFile] = useState<File | null>(null);
  const [mode, setMode] = useState<'existing' | 'new'>('existing');
  const [lists, setLists] = useState<MailwhizList[]>([]);
  const [listsLoading, setListsLoading] = useState(false);
  const [selectedUid, setSelectedUid] = useState('');
  const [newList, setNewList] = useState(emptyNewList);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);

  const token = () => localStorage.getItem('ems_access_token') || localStorage.getItem('token');

  const loadLists = async () => {
    try {
      setListsLoading(true);
      setError(null);
      const res = await fetch(`${API_BASE}/mailwhiz/lists`, {
        headers: token() ? { Authorization: `Bearer ${token()}` } : {}
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || data.error || 'Could not load MailWhiz lists.');
      setLists(data.lists || []);
      if (data.lists?.length) setSelectedUid((prev) => prev || data.lists[0].uid);
    } catch (err: any) {
      setError(`Could not load MailWhiz lists: ${err.message}`);
    } finally {
      setListsLoading(false);
    }
  };

  useEffect(() => {
    loadLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canSubmit = !!file && !uploading &&
    (mode === 'existing'
      ? !!selectedUid
      : !!(newList.name && newList.fromName && newList.fromEmail && newList.companyName && newList.address && newList.city && newList.countryCode));

  const handleImport = async () => {
    if (!file) return;
    try {
      setUploading(true);
      setError(null);
      setResult(null);

      const formData = new FormData();
      formData.append('file', file);
      if (mode === 'existing') {
        formData.append('listUid', selectedUid);
      } else {
        formData.append('newList', JSON.stringify(newList));
      }

      // FormData sets its own multipart Content-Type
      const res = await fetch(`${API_BASE}/mailwhiz/import-csv`, {
        method: 'POST',
        headers: token() ? { Authorization: `Bearer ${token()}` } : {},
        body: formData
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || data.error || 'Import failed.');

      setResult(data);
      if (mode === 'new') {
        setMode('existing');
        setSelectedUid(data.listUid);
        loadLists();
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const setField = (key: keyof typeof emptyNewList) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setNewList({ ...newList, [key]: e.target.value });

  return (
    <Box sx={{ p: 3, maxWidth: 760, mx: 'auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <EmailIcon sx={{ color: '#3b82f6' }} /> MailWhiz Import
      </Typography>
      <Typography variant="body2" sx={{ color: '#475569', mb: 3 }}>
        Upload a leads CSV and add every row with an email to a MailWhiz list. Rows without an email are skipped.
      </Typography>

      <Paper variant="outlined" sx={{ p: 3, borderRadius: '12px', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Box>
          <Button component="label" variant="outlined" startIcon={<UploadIcon />} disabled={uploading} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}>
            {file ? 'Change CSV file' : 'Choose CSV file'}
            <input
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); e.target.value = ''; }}
            />
          </Button>
          {file && (
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 600, color: '#0f172a' }}>
              {file.name} ({(file.size / 1024).toFixed(0)} KB)
            </Typography>
          )}
        </Box>

        <Divider />

        <FormControl>
          <RadioGroup row value={mode} onChange={(e) => setMode(e.target.value as 'existing' | 'new')}>
            <FormControlLabel value="existing" control={<Radio />} label="Add to existing list" />
            <FormControlLabel value="new" control={<Radio />} label="Create new list" />
          </RadioGroup>
        </FormControl>

        {mode === 'existing' ? (
          <Box>
            {listsLoading && <LinearProgress sx={{ mb: 1 }} />}
            <TextField
              select
              fullWidth
              label="MailWhiz list"
              value={selectedUid}
              onChange={(e) => setSelectedUid(e.target.value)}
              disabled={listsLoading || lists.length === 0}
              helperText={!listsLoading && lists.length === 0 ? 'No lists found. Check the MailWhiz settings or create a new list.' : ' '}
            >
              {lists.map((l) => (
                <MenuItem key={l.uid} value={l.uid}>{l.name}</MenuItem>
              ))}
            </TextField>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextField label="List name *" value={newList.name} onChange={setField('name')} />
            <TextField label="Description" value={newList.description} onChange={setField('description')} />
            <TextField label="From name *" value={newList.fromName} onChange={setField('fromName')} />
            <TextField label="From email *" value={newList.fromEmail} onChange={setField('fromEmail')} />
            <TextField label="Reply-to email" helperText="Defaults to the from email" value={newList.replyTo} onChange={setField('replyTo')} />
            <TextField label="Company name *" value={newList.companyName} onChange={setField('companyName')} />
            <TextField label="Address *" value={newList.address} onChange={setField('address')} />
            <TextField label="City *" value={newList.city} onChange={setField('city')} />
            <TextField label="State" value={newList.state} onChange={setField('state')} />
            <TextField label="Country code *" helperText="Two letters, e.g. US" value={newList.countryCode} onChange={setField('countryCode')} />
          </Box>
        )}

        {uploading && <LinearProgress />}
        {error && <Alert severity="error">{error}</Alert>}

        {result && (
          <Alert severity={result.failed ? 'warning' : 'success'}>
            <div>{result.createdNewList ? 'Created a new list and imported' : 'Imported'} {result.added} of {result.emailsFound} emails.</div>
            <div>Rows without a valid email: {result.skippedNoEmail}. Duplicate emails in file: {result.skippedDuplicate}.</div>
            {result.failed > 0 && (
              <div>
                Failed: {result.failed} (often already on the list).
                {result.failures.slice(0, 5).map((f) => (
                  <div key={f.email}>{f.email}: {f.reason}</div>
                ))}
              </div>
            )}
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={!canSubmit}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 3 }}
          >
            {uploading ? 'Importing...' : 'Import to MailWhiz'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default MailwhizImportPage;
